import type { Objective } from '../types'
import { useTranslation } from 'react-i18next'

interface ObjectiveCardProps {
  objective: Objective
  onUpdate: (id: string, status: string) => void
}

const statusConfig: Record<Objective['status'], { className: string }> = {
  pending: { className: 'bg-primary-50 text-primary-600' },
  completed: { className: 'bg-mindeia-success-light text-mindeia-success' },
  abandoned: { className: 'bg-surface-100 text-neutral-600' },
}

export function ObjectiveCard({ objective, onUpdate }: ObjectiveCardProps) {
  const { t, i18n } = useTranslation()
  const { className } = statusConfig[objective.status]

  const formattedDate = objective.target_date
    ? new Date(objective.target_date + 'T12:00:00').toLocaleDateString(i18n.language, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : null

  return (
    <div className="rounded-xl border border-surface-200 bg-white p-5">
      <div className="mb-2 flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold text-neutral-900 leading-snug">
          {objective.title}
        </h3>
        <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${className}`}>
          {t(`objectives.statuses.${objective.status}`)}
        </span>
      </div>

      {objective.description && (
        <p className="mb-3 text-sm text-neutral-600 leading-relaxed">
          {objective.description}
        </p>
      )}

      {formattedDate && (
        <p className="mb-3 text-xs text-neutral-600">
          {t('objectives.deadline')}{' '}
          <span className="font-medium">{formattedDate}</span>
        </p>
      )}

      {objective.status === 'pending' && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onUpdate(objective.id, 'completed')}
            className="rounded-md bg-mindeia-success-light px-3 py-1.5 text-xs font-medium text-mindeia-success hover:opacity-80 transition-opacity"
          >
            {t('objectives.mark_completed')}
          </button>
          <button
            type="button"
            onClick={() => onUpdate(objective.id, 'abandoned')}
            className="rounded-md bg-surface-100 px-3 py-1.5 text-xs font-medium text-neutral-600 hover:bg-surface-200 transition-colors"
          >
            {t('objectives.abandon')}
          </button>
        </div>
      )}
    </div>
  )
}
